import React, { useRef, useEffect, useState, useCallback, useLayoutEffect, useMemo } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { IconButton, makeStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { COLORS, getLevelStyle, formatLogTime } from './logsConstants';
import { getDetailsFromUrl } from '../../../services/blueprint';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setFocusElement, setTabOnFocus, updateLogRedirectionData } from '../../../store/global/actions';
import { getLogHelpers } from '../../../store/global/selectors';
import LogLinkMenu from './LogsLinkMenu';
import useConstructsNavigation from '../../../hooks/useConstructsNavigation';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SourcePill from './SourcePill';
import MessageContent from './MessageContent';

const CELL_V_PADDING = 12; // 11px top + 11px bottom
const LINE_HEIGHT = 19;
const COLLAPSED_LINES = 2;
const COLLAPSED_ROW_HEIGHT = CELL_V_PADDING + COLLAPSED_LINES * LINE_HEIGHT; // kept for CSS clamp math only
const FLASH_DURATION_MS = 3000;
const INITIAL_INDEX = 1_000_000; // large starting point so firstItemIndex can count down as logs are prepended

const COLUMNS = [
  { key: 'time', label: 'Time', width: 150 },
  { key: 'level', label: 'Severity', width: 100 },
  { key: 'source', label: 'Type', width: 170 },
  { key: 'instance', label: 'Source', width: 250 },
  { key: 'msg', label: 'Message', width: null },
];

const LOG_LEVEL = {
  1: "Basic",
  2: "Verbose",
  3: "Detailed",
  4: "Debug"
}

const useStyles = makeStyles(() => ({
  wrapper: {
    position: 'relative',
    height: '100%',
    width: '100%',
    minHeight: 0,
  },
  headerRow: {
    display: 'flex',
    backgroundColor: '#fafbfc',
    borderBottom: `1px solid ${COLORS.border}`,
  },
  headerCell: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: COLORS.textMuted,
    padding: '9px 24px',
    boxSizing: 'border-box',
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    borderBottom: `1px solid ${COLORS.border}`,
    boxSizing: 'border-box',
    cursor: 'pointer',
  },
  '@keyframes logRowFlash': {
    '0%': { backgroundColor: COLORS.accentBg },
    '100%': { backgroundColor: 'transparent' },
  },
  rowFlash: {
    animation: '$logRowFlash 3s ease-out',
  },
  cell: {
    padding: '6px 24px',
    overflow: 'hidden',
    boxSizing: 'border-box',
  },
  levelBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontWeight: 500,
    fontSize: '12.5px',
    whiteSpace: 'nowrap',
    textTransform: 'capitalize',
  },
  levelIcon: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    flexShrink: 0,
  },
  timeCell: {
    fontSize: 12,
    color: COLORS.textSecondary,
  },
  sourceCell: {
    fontSize: 12.5,
    color: COLORS.textPrimary,
    overflow: 'hidden',
  },
  sourceType: {
    fontWeight: 500,
  },
  sourceApp: {
    color: COLORS.textMuted,
    fontSize: 11,
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  msgCellWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    flex: 1,
    minWidth: 0,
  },
  msgCell: {
    color: COLORS.textPrimary,
    fontSize: 13,
    lineHeight: `${LINE_HEIGHT}px`,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    flex: 1,
    minWidth: 0,
  },
  msgCollapsed: {
    display: '-webkit-box',
    WebkitLineClamp: COLLAPSED_LINES,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  expandIcon: {
    flexShrink: 0,
    color: COLORS.textMuted,
    marginTop: 2,
  },
  empty: {
    padding: '48px 24px',
    textAlign: 'center',
    color: COLORS.textSecondary,
  },
  linkIcon: {
    fontSize: '15px',
    position: 'relative',
    top: '1px',
    marginRight: '4px',
    color: 'blue',
  },
  newPill: {
    position: 'absolute',
    top: 10,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 999,
    backgroundColor: COLORS.accent,
    color: '#ffffff',
    fontSize: 12.5,
    fontWeight: 600,
    boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
    cursor: 'pointer',
    border: 'none',
  },
  icon: {
    padding: 3,
    marginLeft: 4,
    color: COLORS.accent,
    verticalAlign: 'middle',
  },
  highlight: {
    backgroundColor: '#FEF08A',
    color: 'inherit',
    borderRadius: 2,
    padding: '0 1px',
    fontWeight: 600,
  },
  linkPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.4,
    color: '#1a73e8',
    backgroundColor: 'rgba(26, 115, 232, 0.08)',
    border: '1px solid rgba(26, 115, 232, 0.24)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    marginRight: 6,
    '&:hover': {
      backgroundColor: 'rgba(26, 115, 232, 0.16)',
    },
  },
}));

function Row({ log, classes, expandedSet, toggleExpand, flashSet, closeModal, navigateToConstruct, query }) {
  const { slug, blueprintId } = getDetailsFromUrl();
  const navigate = useHistory();
  const dispatch = useDispatch();
  const logHelpers = useSelector(getLogHelpers);
  const l = log;
  const levelStyle = getLevelStyle(l.level);
  const isExpanded = expandedSet.has(l.ts);
  const isFlashing = flashSet.has(l.ts);
  const rowRef = useRef(null);
  const msgRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const parseLogPrefix = useCallback((log) => {
    if (typeof log !== 'string') return null;
    console.log({logHelpers})
    const match = log.match(/^\|((?:[\w]+:[^|;]*\|?)+);([\s\S]*)$/);
    if (!match) return null;
  
    const [, header, message] = match;
    const result = {};
  
    header.split('|').filter(Boolean).forEach(pair => {
      const idx = pair.indexOf(':');
      const key = pair.slice(0, idx).trim();
      const value = pair.slice(idx + 1).trim();
      result[key] = key === 'lineNumber' ? Number(value) : value;
    });

    result.message = message;
    return result;
  },[logHelpers])

  const parsed = useMemo(
    () => parseLogPrefix(l.msg) ?? { message: l.msg },
    [l.msg]
  );
  
  const hyperlinks = useMemo(() => {
    const transaction = parsed.transaction
      ? logHelpers?.transactionNameMap?.[parsed.transaction]
      : undefined;

    const event = parsed.event
      ? (logHelpers?.eventNameMap?.[parsed.event] ?? []).map(e => ({
          ...e,
          transaction: logHelpers?.transactionEventMap?.[e._id],
        }))
      : undefined;

    const baseHandler = parsed.handler
      ? logHelpers?.handlerIdentifier?.[parsed.handler]
      : undefined;
    const handler = baseHandler
      ? { ...baseHandler, lineNumber: parsed.lineNumber }
      : undefined;

    let obj = {};

    if(transaction) obj.transaction = transaction;
    if(event?.length > 0) obj.event = event;
    if(handler) obj.handler = handler;

    return obj;
  }, [parsed, logHelpers]);

  const buildLinkPills = ({ transaction, event, handler } = {}) => {
    const pills = [];
  
    if (transaction?._id) {
      pills.push({
        key: `txn-${transaction._id}`,
        label: `Transaction: ${transaction.name}`,
        link: { type: 'TRANSACTION', transactionId: transaction._id },
      });
    }
  
    if (Array.isArray(event)) {
      const showTxn = event.length > 1; // disambiguate when the same event name exists in several transactions
      event.forEach(ev => {
        if (!ev?._id) return;
        pills.push({
          key: `ev-${ev._id}`,
          label: showTxn && ev.transaction?.name
            ? `Event: ${ev.transaction.name} → ${ev.name}`
            : `Event: ${ev.name}`,
          link: { type: 'EVENT', eventId: ev._id, transactionId: ev.transaction?._id },
        });
      });
    }
  
    if (handler?.eventId) {
      const type = handler.handler?.type; // "onrecv" | "onsend"
      const event = logHelpers.eventMap?.[handler.eventId];
      const line = handler.lineNumber != null ? ` · L${handler.lineNumber}` : '';
      pills.push({
        key: `hd-${handler.handler?._id ?? handler.eventId}`,
        label: `Handler: ${type}${event ? ` (${event?.name})` : ''}${line}`,
        link: {
          type: 'HANDLER',
          handlerType: type,
          eventId: handler.eventId,
          transactionId: handler.transactionId,
          lineNumber: handler.lineNumber,
        },
      });
    }
  
    return pills;
  };

  const linkPills = useMemo(() => buildLinkPills(hyperlinks), [hyperlinks]);

  // Only measure overflow while collapsed (clamp is applied). Once expanded, the clamp
  // is removed so scrollHeight === clientHeight and we'd wrongly conclude "no overflow" —
  // so skip re-measuring and keep whatever we last determined.
  useLayoutEffect(() => {
    if (isExpanded) return;
    const el = msgRef.current;
    if (!el) return;
    setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [l.msg, query, isExpanded]);

  const navigateToEvent = (details) => {
    const eventMap = logHelpers?.eventMap;
    const path = `/blueprint/${slug}/${blueprintId}/transaction/${details.transactionId}`;
    navigate.push(path);
    dispatch(setTabOnFocus(0))
    dispatch(setFocusElement({
      transaction: details.transactionId,
      type: 'event',
      id: details.eventId,
      name: eventMap[details.eventId]?.name || '',
      mode: "edit",
      item: eventMap[details.eventId]
    }));
  };

  const navigateToHandler = (details) => {
    const eventMap = logHelpers?.eventMap;
    const path = `/blueprint/${slug}/${blueprintId}/transaction/${details.transactionId}`;
    navigate.push(path);
    dispatch(setFocusElement({
      transaction: details.transactionId,
      type: 'event',
      id: details.eventId,
      name: eventMap[details.eventId]?.name || '',
      mode: "edit",
      item: eventMap[details.eventId]
    }));

    dispatch(setTabOnFocus(details.handlerType === "onsend" ? 4 : 5));
  };

  const navigateToTransaction = (details) => {
    const path = `/blueprint/${slug}/${blueprintId}/transaction/${details.transactionId}`;
    navigate.push(path);
  };

  const handleNavigation = (event, log, message, level) => {
    event.stopPropagation();
    closeModal()
    if (log.link) {
      const TYPE = log.link.type;
      let dataToRetain;
      switch (TYPE) {
        case 'EVENT':
          dataToRetain = {
            entity: 'event',
            entityId: log.link?.eventId,
            parent: 'transaction',
            parentId: log.link?.transactionId,
            level,
            message
          }
          dispatch(updateLogRedirectionData(dataToRetain))
          navigateToEvent(log.link);
          break;
        case "HANDLER":
          dataToRetain = {
            entity: log.link?.handlerType === "onrecv" ? 'onrecv_handler' : 'onsend_handler',
            entityId: log.link?.eventId,
            parent: 'transaction',
            parentId: log.link?.transactionId,
            level,
            message
          }
          dispatch(updateLogRedirectionData(dataToRetain))
          navigateToHandler(log.link);
          break;
        case "TRANSACTION":
          navigateToTransaction(log.link);
          break;
        default:
          break;
      }
    } else if (log.src === "Validator") {
      const dataToRetain = {
        entity: log.entityType,
        entityId: log.entityId,
        parent: log.mainEntityType,
        parentId: log.mainEntityId,
        message: log.message,
        tabOnfocus: log.tabOnfocus,
        source: 'Validator',
        level
      }
      dispatch(updateLogRedirectionData(dataToRetain))
      if (log?.mainEntityType === "view") {
        localStorage.setItem("VIEW_TAB_ONFOCUS", "datasource");
        navigateToConstruct({
          mainEntityId: log?.mainEntityId,
          mainEntityType: log?.mainEntityType,
        });

      } else if (log?.mainEntityType === "testsuite") {
        const testsuite = log?.entityItem;
        const testcase = testsuite?.testcase;

        const isContextValidation = testsuite?.context_validation || false;
        const isTestcaseContext = testsuite?.testcase_context;


        const isEventValidation = testsuite?.event_validation || false;
        const focusEvent = isEventValidation && testsuite.event ? testsuite.event : null;

        navigateToConstruct({
          mainEntityId: log?.mainEntityId,
          mainEntityType: log?.mainEntityType,
          subEntity: testcase,
          entityId: testcase._id.toString(),
          entityType: isEventValidation ? "eventPayload" : isTestcaseContext ? "testcase" : "testsuite",
          entityItem: isEventValidation ? focusEvent : isTestcaseContext ? testcase : testsuite,
          tabOnfocus: log?.tabOnfocus,
        });

      } else {
        navigateToConstruct({
          mainEntityId: log?.mainEntityId,
          mainEntityType: log?.mainEntityType,
          entityId: log?.entityId,
          entityType: log?.entityType,
          entityItem: log?.entityItem,
          tabOnfocus: log?.tabOnfocus || 0
        });
      }
    }
  };

  const manageLinks = (links) => {
    const eventMap = logHelpers?.eventMap;
    const transactionEventMap = logHelpers?.transactionEventMap;

    let editedLinks = links;

    if (Array.isArray(links)) {
      if (links[0]?.type === "EVENT") {
        editedLinks = links.map(item => {
          const event = eventMap[item.eventId]
          const transaction = transactionEventMap[item.eventId]
          return {
            link: item,
            desc: `${transaction.name} → ${event.name}`
          }
        })
      }
    }
    return editedLinks
  }

  const handleRowExpansion = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;
    if (isExpanded || isOverflowing || Object.keys(hyperlinks).length > 0) toggleExpand(l.ts);
  }

  return (
    <div className={`${classes.row} log-row ${isFlashing ? classes.rowFlash : ''}`} onClick={handleRowExpansion}>
      <div ref={rowRef} style={{ display: 'flex', width: '100%' }}>
        <div className={`${classes.cell} ${classes.timeCell}`} style={{ width: COLUMNS[0].width, flexShrink: 0 }}>
          {l.ts ? formatLogTime(l.ts, l.src === "Runtime" || l.src === "Simulation") : '—'}
        </div>

        <div className={classes.cell} id="level-content" style={{ width: COLUMNS[1].width, flexShrink: 0 }}>
          <span className={classes.levelBadge} style={{ color: levelStyle.color }}>
            <span className={classes.levelIcon} style={{ backgroundColor: levelStyle.color, textTransform: 'lowercase' }}>
              {levelStyle.symbol}
            </span>
            {l.level || '—'}
          </span>
        </div>

        {/* <div className={classes.cell} style={{ width: COLUMNS[2].width, flexShrink: 0 }}>
          {l.loglevel ? LOG_LEVEL[l.loglevel] : '—'}
        </div> */}
        

        <div className={`${classes.cell} ${classes.sourceCell} log-source`} style={{ width: COLUMNS[2].width, flexShrink: 0 }}>
          <SourcePill src={l.src} />
        </div>

        <div className={`${classes.cell} ${classes.sourceCell}`} style={{ width: COLUMNS[3].width, flexShrink: 0, wordBreak: 'break-all' }}>
          {l.linstance ? `${l.ltype?.split('.')[l.ltype?.split('.')?.length - 1]}/${l.linstance}` : '—'}
        </div>

        <div className={classes.cell} style={{ flex: 1, minWidth: 0 }}>
          <div className={classes.msgCellWrapper}>
            <div ref={msgRef} className={`${classes.msgCell} msg-content ${isExpanded ? '' : classes.msgCollapsed}`}>
              {
                l.src === "Validator" &&
                <IconButton
                  size="small"
                  className={classes.icon}
                  id='redirect-button'
                  onClick={(e) => handleNavigation(e, l, l.msg, l.level)}
                >
                  <OpenInNewIcon style={{ fontSize: 14 }} />
                </IconButton>
              }
              {l.link && (
                <LogLinkMenu
                  heading='Select a destination'
                  items={manageLinks(l.link)}
                  onSelect={(e, item) => handleNavigation(e, { link: item }, l.msg, l.level)}
                />
              )}
              <MessageContent text={parsed.message} query={query} />

              {isExpanded && linkPills.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  paddingTop: '5px',
                  borderTop: '1px solid #8080803b',
                  gap: '8px',
                  marginTop: '5px'
                }}>
                  {linkPills.map(pill => (
                    <span
                      key={pill.key}
                      className={classes.linkPill}
                      onClick={(e) => handleNavigation(e, { link: pill.link }, parsed.message, l.level)}
                    >
                      <OpenInNewIcon style={{ fontSize: 12 }} />
                      <span>{pill.label}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
            {(isExpanded || isOverflowing || Object.keys(hyperlinks).length > 0) && (
              isExpanded ? (
                <ExpandLessIcon fontSize="small" className={classes.expandIcon} />
              ) : (
                <ExpandMoreIcon fontSize="small" className={classes.expandIcon} />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function LogsTableBody({ logs, classes, width, height, listRef, setPendingCount, pendingCount, closeModal, navigateToConstruct, query }) {
  const [expandedSet, setExpandedSet] = useState(() => new Set()); // keyed by log.ts, not index
  const [flashSet, setFlashSet] = useState(() => new Set());
  const [firstItemIndex, setFirstItemIndex] = useState(INITIAL_INDEX);
  const isAtTopRef = useRef(true);
  const prevTopTsRef = useRef(logs.length > 0 ? logs[0].ts : null);
  const isFirstRenderRef = useRef(true);

  const toggleExpand = useCallback((ts) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(ts)) {
        next.delete(ts);
      } else {
        next.add(ts);
      }
      return next;
    });
  }, []);
  // No manual size cache to invalidate here — Virtuoso measures each row's real
  // height via ResizeObserver and re-measures automatically when content changes.

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevTopTsRef.current = logs.length > 0 ? logs[0].ts : null;
      return;
    }
    if (logs.length === 0) return;

    const prevTopTs = prevTopTsRef.current;
    let newCount = 0;
    if (prevTopTs) {
      const idx = logs.findIndex((l) => l.ts <= prevTopTs);
      newCount = idx === -1 ? logs.length : idx;
    }
    prevTopTsRef.current = logs[0].ts;

    if (newCount === 0) return;

    // expandedSet is keyed by log.ts, so prepending new items needs no re-keying.
    setFirstItemIndex((i) => i - newCount); // keeps Virtuoso's scroll position visually anchored

    const newTsSet = new Set(logs.slice(0, newCount).map((l) => l.ts));
    setFlashSet((prev) => new Set([...prev, ...newTsSet]));
    setTimeout(() => {
      setFlashSet((prev) => {
        const next = new Set(prev);
        newTsSet.forEach((ts) => next.delete(ts));
        return next;
      });
    }, FLASH_DURATION_MS);

    if (!isAtTopRef.current) {
      setPendingCount((c) => c + newCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs]);

  const handleAtTopChange = useCallback((atTop) => {
    isAtTopRef.current = atTop;
    if (atTop && pendingCount > 0) {
      setPendingCount(0);
    }
  }, [pendingCount]);

  return (
    <Virtuoso
      ref={listRef}
      style={{ height, width }}
      data={logs}
      firstItemIndex={firstItemIndex}
      initialTopMostItemIndex={0}
      computeItemKey={(_, log) => log.ts}
      atTopStateChange={handleAtTopChange}
      followOutput={false}
      components={{
        List: React.forwardRef((props, ref) => (
          <div
            {...props}
            ref={ref}
            id="validation-log-list"
            className="validation-log-list"
          />
        )),
      }}
      itemContent={(_, log) => (
        <Row
          log={log}
          classes={classes}
          expandedSet={expandedSet}
          toggleExpand={toggleExpand}
          flashSet={flashSet}
          closeModal={closeModal}
          navigateToConstruct={navigateToConstruct}
          query={query}
        />
      )}
    />
  );
}

export default function LogsTable({ logs, closeModal, isLoading, query }) {
  const classes = useStyles();
  const listRef = useRef(null);
  const [pendingCount, setPendingCount] = useState(0);
  const { navigateToConstruct } = useConstructsNavigation();

  function jumpToNew() {
    if (listRef.current) listRef.current.scrollToIndex({ index: 0, align: 'start', behavior: 'auto' });
    setPendingCount(0);
  }

  return (
    <div style={{ position: "relative", display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {pendingCount > 0 && (
        <button className={classes.newPill} onClick={jumpToNew}>
          <ArrowUpwardIcon style={{ fontSize: 14 }} />
          {pendingCount} new log{pendingCount === 1 ? '' : 's'}
        </button>
      )}
      <div className={classes.headerRow}>
        {COLUMNS.map((c) => (
          <div
            key={c.key}
            className={classes.headerCell}
            style={c.width ? { width: c.width, flexShrink: 0 } : { flex: 1 }}
          >
            {c.label}
          </div>
        ))}
      </div>

      {isLoading ? (
        <div className={classes.empty}>Loading logs...</div>
      ) : logs.length === 0 ? (
        <div className={classes.empty}>No logs to display.</div>
      ) : (
        <div style={{ flex: 1, minHeight: 0 }}>
          <LogsTableBody
            navigateToConstruct={navigateToConstruct}
            closeModal={closeModal}
            logs={logs}
            setPendingCount={setPendingCount}
            pendingCount={pendingCount}
            listRef={listRef}
            classes={classes}
            width="100%"
            height="100%"
            query={query}
          />
        </div>
      )}
    </div>
  );
}